import { useCallback, useEffect, useState } from "react";

/**
 * useArmRoutingTemplates — iter-208
 *
 * Wraps the 5 endpoints under /api/arm-routing/templates with optimistic
 * UI for like-toggle and silent fire-and-forget for use-counter. Mirrors
 * the iter-175 useSunoTemplates hook so the hook surface is consistent.
 *
 * Returns:
 *   {
 *     templates,      // [{ id, title, ... }]
 *     loading,        // boolean
 *     error,          // string | null
 *     sort,           // current sort key
 *     setSort,        // (sort) => void  triggers refetch
 *     category,       // current category filter (or null)
 *     setCategory,    // (cat | null) => void  triggers refetch
 *     publish,        // ({title,description,category,channels,sourceMap}) => Promise<entry>
 *     remove,         // (id) => Promise<boolean>
 *     toggleLike,     // (id) => Promise<void>
 *     markUsed,       // (id) => Promise<void>  (silent, no error toast)
 *     refresh,        // () => void
 *   }
 */
export default function useArmRoutingTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sort, setSort] = useState("popular");
  const [category, setCategory] = useState(null);
  const [refreshTick, setRefreshTick] = useState(0);

  const backend = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const qs = new URLSearchParams();
        qs.set("sort", sort);
        if (category) qs.set("category", category);
        const r = await fetch(`${backend}/api/arm-routing/templates/public?${qs.toString()}`, {
          credentials: "include",
        });
        if (!r.ok) throw new Error(`Load failed: ${r.status}`);
        const data = await r.json();
        if (!cancelled) setTemplates(Array.isArray(data) ? data : []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to load community presets");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [backend, sort, category, refreshTick]);

  const refresh = useCallback(() => setRefreshTick((t) => t + 1), []);

  const publish = useCallback(async ({ title, description, category: cat, channels, sourceMap }) => {
    const r = await fetch(`${backend}/api/arm-routing/templates/publish`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title,
        description: description || "",
        category: cat || "other",
        channels: Array.isArray(channels) ? channels : [],
        source_map: Object.fromEntries(
          Object.entries(sourceMap || {}).map(([k, v]) => [String(k), Number(v)]),
        ),
      }),
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.detail || `Publish failed: ${r.status}`);
    }
    const entry = await r.json();
    // Prepend to local list so the UI feels instant — refresh will reconcile.
    setTemplates((prev) => [entry, ...prev]);
    return entry;
  }, [backend]);

  const remove = useCallback(async (id) => {
    const r = await fetch(`${backend}/api/arm-routing/templates/${id}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (!r.ok) {
      const body = await r.json().catch(() => ({}));
      throw new Error(body.detail || `Delete failed: ${r.status}`);
    }
    setTemplates((prev) => prev.filter((t) => t.id !== id));
    return true;
  }, [backend]);

  const toggleLike = useCallback(async (id) => {
    // Optimistic: flip heart + adjust counter immediately, revert on failure.
    let prevSnapshot = null;
    setTemplates((prev) => {
      prevSnapshot = prev;
      return prev.map((t) => t.id === id
        ? {
            ...t,
            liked_by_me: !t.liked_by_me,
            like_count: Math.max(0, t.like_count + (t.liked_by_me ? -1 : 1)),
          }
        : t);
    });
    try {
      const r = await fetch(`${backend}/api/arm-routing/templates/${id}/like`, {
        method: "POST",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`Like failed: ${r.status}`);
      const data = await r.json();
      // Sync server truth back in case our optimistic adjust was wrong.
      setTemplates((prev) => prev.map((t) => t.id === id
        ? { ...t, liked_by_me: !!data.liked_by_me, like_count: Number(data.like_count) || 0 }
        : t));
    } catch (e) {
      // Revert optimistic update on failure.
      if (prevSnapshot) setTemplates(prevSnapshot);
      throw e;
    }
  }, [backend]);

  const markUsed = useCallback(async (id) => {
    // Fire-and-forget — usage counter is a vanity metric; we don't toast on failure.
    try {
      await fetch(`${backend}/api/arm-routing/templates/${id}/use`, {
        method: "POST",
        credentials: "include",
      });
    } catch (e) {
      void e;
    }
  }, [backend]);

  return {
    templates, loading, error,
    sort, setSort, category, setCategory,
    publish, remove, toggleLike, markUsed, refresh,
  };
}
